import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Tabs, Tab, Snackbar, Alert
} from '@mui/material';
import {
  Add as AddIcon, Refresh as RefreshIcon,
  Campaign as CampaignIcon, SettingsSuggest as TriggerIcon
} from '@mui/icons-material';

import {
  NotificationItem, NotificationStats, TriggerRule,
  CreateNotificationPayload, CreateTriggerPayload
} from './types';
import {
  fetchNotificationsList, fetchPharmaciesList, createNotificationApi,
  toggleNotificationActiveApi, deleteNotificationApi, fetchNotificationStatsApi,
  fetchTriggersList, createTriggerApi, toggleTriggerApi, deleteTriggerApi,
  runTriggerApi
} from './api';

import FilterToolbar from './components/FilterToolbar';
import NotificationTable from './components/NotificationTable';
import TriggerTable from './components/TriggerTable';
import CreateNotificationDialog from './components/CreateNotificationDialog';
import CreateTriggerDialog from './components/CreateTriggerDialog';
import NotificationStatsDialog from './components/NotificationStatsDialog';

const AdminNotifications: React.FC = () => {
  const [activeTab, setActiveTab] = useState<number>(0);

  // Список уведомлений
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  // Фильтры
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [targetFilter, setTargetFilter] = useState<string>('');

  // Список аптек
  const [availablePharmacies, setAvailablePharmacies] = useState<string[]>([]);

  // Диалоги
  const [createDialogOpen, setCreateDialogOpen] = useState<boolean>(false);
  const [creating, setCreating] = useState<boolean>(false);

  const [statsDialogOpen, setStatsDialogOpen] = useState<boolean>(false);
  const [statsLoading, setStatsLoading] = useState<boolean>(false);
  const [statsData, setStatsData] = useState<NotificationStats | null>(null);

  // Триггеры
  const [triggers, setTriggers] = useState<TriggerRule[]>([]);
  const [triggersLoading, setTriggersLoading] = useState<boolean>(false);
  const [createTriggerOpen, setCreateTriggerOpen] = useState<boolean>(false);

  // Уведомления системы (Snackbar)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info'
  });

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadPharmacies = async () => {
    try {
      const list = await fetchPharmaciesList();
      setAvailablePharmacies(list);
    } catch (err) {
      console.error('Ошибка загрузки списка аптек:', err);
    }
  };

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchNotificationsList({
        page: page + 1,
        limit: rowsPerPage,
        status: statusFilter || undefined,
        targetType: targetFilter || undefined,
        search: searchFilter || undefined
      });
      if (data.success) {
        setNotifications(data.notifications || []);
        setTotalCount(data.total || 0);
      }
    } catch (err) {
      console.error('Ошибка загрузки уведомлений:', err);
      showSnackbar('Не удалось загрузить список уведомлений', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, targetFilter, searchFilter]);

  const loadTriggers = async () => {
    setTriggersLoading(true);
    try {
      const list = await fetchTriggersList();
      setTriggers(list);
    } catch (err) {
      console.error('Ошибка загрузки триггеров:', err);
      showSnackbar('Не удалось загрузить триггеры', 'error');
    } finally {
      setTriggersLoading(false);
    }
  };

  useEffect(() => {
    loadPharmacies();
  }, []);

  useEffect(() => {
    if (activeTab === 0) {
      loadNotifications();
    } else {
      loadTriggers();
    }
  }, [activeTab, loadNotifications]);

  const handleCreateNotification = async (payload: CreateNotificationPayload) => {
    setCreating(true);
    try {
      const res = await createNotificationApi(payload);
      if (res.success) {
        showSnackbar('Уведомление создано');
        setCreateDialogOpen(false);
        loadNotifications();
      } else {
        showSnackbar(res.message || 'Ошибка создания', 'error');
      }
    } catch (err) {
      console.error('Ошибка создания уведомления:', err);
      showSnackbar('Ошибка сервера при создании', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (id: number, currentStatus: boolean) => {
    try {
      const res = await toggleNotificationActiveApi(id, !currentStatus);
      if (res.success) {
        setNotifications(prev =>
          prev.map(item => (item.id === id ? { ...item, isActive: !currentStatus } : item))
        );
      }
    } catch (err) {
      console.error('Ошибка переключения статуса:', err);
      showSnackbar('Не удалось изменить статус', 'error');
    }
  };

  const handleDeleteNotification = async (id: number) => {
    if (!window.confirm('Удалить уведомление?')) return;
    try {
      const res = await deleteNotificationApi(id);
      if (res.success) {
        showSnackbar('Уведомление удалено');
        loadNotifications();
      }
    } catch (err) {
      console.error('Ошибка при удалении:', err);
      showSnackbar('Ошибка удаления', 'error');
    }
  };

  const handleOpenStats = async (id: number) => {
    setStatsLoading(true);
    setStatsDialogOpen(true);
    try {
      const res = await fetchNotificationStatsApi(id);
      setStatsData(res);
    } catch (err) {
      console.error('Ошибка загрузки статистики:', err);
      showSnackbar('Не удалось загрузить данные прочтений', 'error');
    } finally {
      setStatsLoading(false);
    }
  };

  const handleToggleTrigger = async (id: number, currentEnabled: boolean) => {
    try {
      const res = await toggleTriggerApi(id, !currentEnabled);
      if (res.success) {
        setTriggers(prev =>
          prev.map(t => (t.id === id ? { ...t, isEnabled: !currentEnabled } : t))
        );
      }
    } catch (err) {
      console.error('Ошибка переключения триггера:', err);
      showSnackbar('Ошибка при переключении', 'error');
    }
  };

  const handleCreateTrigger = async (payload: CreateTriggerPayload) => {
    try {
      const res = await createTriggerApi(payload);
      if (res.success) {
        showSnackbar('Триггер сохранен');
        setCreateTriggerOpen(false);
        loadTriggers();
      }
    } catch (err) {
      console.error('Ошибка создания триггера:', err);
      showSnackbar('Не удалось создать триггер', 'error');
    }
  };

  const handleDeleteTrigger = async (id: number) => {
    if (!window.confirm('Удалить триггер?')) return;
    try {
      const res = await deleteTriggerApi(id);
      if (res.success) {
        showSnackbar('Триггер удален');
        loadTriggers();
      }
    } catch (err) {
      console.error('Ошибка удаления триггера:', err);
      showSnackbar('Ошибка при удалении', 'error');
    }
  };

  const handleRunTrigger = async (id: number) => {
    try {
      showSnackbar('Запуск проверки базы данных по логике CheckUsersOnDay...', 'info');
      const res = await runTriggerApi(id);
      if (res.success) {
        if (res.count > 0) {
          const listPreview = res.pharmacies && res.pharmacies.length > 0
            ? ` (Аптеки: ${res.pharmacies.slice(0, 8).join(', ')}${res.pharmacies.length > 8 ? ` и еще ${res.pharmacies.length - 8}` : ''})`
            : '';
          showSnackbar(`Проверка выполнена! Найдено ${res.count} аптек без отчета за вчера${listPreview}. Уведомление отправлено!`, 'success');
        } else {
          showSnackbar(res.message || 'Все аптеки заполнили отчет за вчера, нарушений нет!', 'success');
        }
        loadTriggers();
        loadNotifications();
      } else {
        showSnackbar(res.message || 'Ошибка выполнения триггера', 'error');
      }
    } catch (err: any) {
      console.error('Ошибка запуска триггера:', err);
      showSnackbar(err?.response?.data?.message || 'Ошибка при запуске триггера', 'error');
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Тулбар вкладок и действий */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 1.5,
        borderBottom: '1px solid #e2e8f0',
        flexWrap: 'wrap',
        gap: 1
      }}>
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          sx={{
            minHeight: 36,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: '#64748b',
              minHeight: 36,
              py: 0.5,
              px: 1.5,
              '&.Mui-selected': { color: '#2563eb' }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#2563eb',
              height: 2.5
            }
          }}
        >
          <Tab icon={<CampaignIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="Рассылки" />
          <Tab icon={<TriggerIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="Авто-триггеры" />
        </Tabs>

        <Box sx={{ display: 'flex', gap: 1, pb: 0.5 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
            onClick={() => (activeTab === 0 ? loadNotifications() : loadTriggers())}
            sx={{
              color: '#475569',
              borderColor: '#cbd5e1',
              textTransform: 'none',
              borderRadius: '6px',
              fontSize: '0.8rem',
              py: 0.3,
              px: 1.2,
              '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' }
            }}
          >
            Обновить
          </Button>

          {activeTab === 0 ? (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{
                bgcolor: '#2563eb',
                color: 'white',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '6px',
                fontSize: '0.8rem',
                py: 0.3,
                px: 1.5,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#1d4ed8', boxShadow: 'none' }
              }}
            >
              Создать
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={() => setCreateTriggerOpen(true)}
              sx={{
                bgcolor: '#16a34a',
                color: 'white',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '6px',
                fontSize: '0.8rem',
                py: 0.3,
                px: 1.5,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#15803d', boxShadow: 'none' }
              }}
            >
              Добавить триггер
            </Button>
          )}
        </Box>
      </Box>

      {/* Вкладка 0: Рассылки */}
      {activeTab === 0 && (
        <>
          <FilterToolbar
            search={searchFilter}
            onSearchChange={setSearchFilter}
            status={statusFilter}
            onStatusChange={setStatusFilter}
            target={targetFilter}
            onTargetChange={setTargetFilter}
            onReset={() => {
              setSearchFilter('');
              setStatusFilter('');
              setTargetFilter('');
            }}
          />

          <NotificationTable
            notifications={notifications}
            loading={loading}
            totalCount={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={newRows => {
              setRowsPerPage(newRows);
              setPage(0);
            }}
            onToggleActive={handleToggleActive}
            onDelete={handleDeleteNotification}
            onOpenStats={handleOpenStats}
          />
        </>
      )}

      {/* Вкладка 1: Авто-триггеры */}
      {activeTab === 1 && (
        <TriggerTable
          triggers={triggers}
          loading={triggersLoading}
          onToggle={handleToggleTrigger}
          onDelete={handleDeleteTrigger}
          onRun={handleRunTrigger}
        />
      )}

      {/* Диалог создания уведомления */}
      <CreateNotificationDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateNotification}
        availablePharmacies={availablePharmacies}
        creating={creating}
      />

      {/* Диалог статистики прочтений */}
      <NotificationStatsDialog
        open={statsDialogOpen}
        onClose={() => setStatsDialogOpen(false)}
        statsData={statsData}
        loading={statsLoading}
      />

      {/* Диалог создания триггера */}
      <CreateTriggerDialog
        open={createTriggerOpen}
        onClose={() => setCreateTriggerOpen(false)}
        onSubmit={handleCreateTrigger}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%', borderRadius: '6px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminNotifications;
